import { MainSection } from "@/styles/global";
import React from "react";
import S from "./FormContributionSection.styled";
import axios from "axios";
import FormSuccess from "@/components/global-components/FormSuccess";
import { useForm } from "react-hook-form";

export interface FormContributionSectionProps {}

type FormData = {
  dovas_request_category: string;
  sektion: string;
  namn: string;
  email: string;
  soktTidigarePeriod: string;
  redovisaAktivitet?: string;
  beviljadSumma?: string;
  anvandSumma?: string;
  aterbetalning?: string;
  verksamhet: string;
  antalDeltagare: string;
  kostnader: string;
  totalKostnad: string;
  accept: boolean;
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

const emptyForm = (): FormData => ({
  dovas_request_category: "Sektionsbidrag",
  sektion: "",
  namn: "",
  email: "",
  soktTidigarePeriod: "",
  verksamhet: "",
  antalDeltagare: "",
  kostnader: "",
  totalKostnad: "",
  accept: false,
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

const FormContributionSection: React.FC<FormContributionSectionProps> = () => {
  const [formState, setFormState] = React.useState(emptyFormState());

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // update form data
  const updateFormProperty = <K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ): void => {
    setFormState((state: FormState) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  };

  const submitForm = handleSubmit(() => {
    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));
    axios
      .post(process.env.GATSBY_CONTACT_URL || "", {
        ...formState.data,
      })
      .then((res) => {
        if (!res.data.success) {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            error: res.data.error_msg,
          }));
        } else {
          setFormState((state) => ({
            ...state,
            isLoading: false,
            success: true,
          }));
        }
      });
  });

  return (
    <MainSection>
      <S.Inner>
        {formState.success ? (
          <FormSuccess />
        ) : (
          <S.FormWrapper>
            <S.H4>Sektionsbidrag</S.H4>
            <S.Wrapper>
              <S.DetailsLarge>Obligatoriska fält</S.DetailsLarge>
            </S.Wrapper>
            <S.Form onSubmit={submitForm}>
              <S.Wrapper>
                <S.Label>Sektionens namn</S.Label>
                <S.Select
                  {...register("sektion", { required: true })}
                  defaultValue={""}
                  className={errors.sektion ? "error" : ""}
                  onChange={(e) =>
                    updateFormProperty("sektion", e.target.value)
                  }
                >
                  <option value="" disabled selected hidden>
                    Välj sektion
                  </option>
                  <option value="Familjeklubben Näckrosen">
                    Familjeklubben Näckrosen
                  </option>
                  <option value="Familjeklubben Snoken">
                    Familjeklubben Snoken
                  </option>
                  <option value="Dövas Internationella sektion i Stockholm">
                    Dövas Internationella sektion i Stockholm
                  </option>
                  <option value="Schack 46">Schack 46</option>
                  <option value="Fiskeklubben Strömmen">
                    Fiskeklubben Strömmen
                  </option>
                  <option value="Södertörnssektionenn">
                    Södertörnssektionenn
                  </option>
                </S.Select>
                {errors.sektion && (
                  <S.Error role="alert">Sektion är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="name">Ditt namn</S.Label>
                <S.Input
                  type="text"
                  {...register("namn", { required: true })}
                  className={errors.namn ? "error" : ""}
                  placeholder="Ditt namn"
                  value={formState.data.namn}
                  onChange={(e) => updateFormProperty("namn", e.target.value)}
                />
                {errors.namn && (
                  <S.Error role="alert">Namn är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="email">E-post</S.Label>
                <S.Input
                  type="email"
                  {...register("email", { required: true })}
                  className={errors.email ? "error" : ""}
                  placeholder="E-post"
                  onChange={(e) => updateFormProperty("email", e.target.value)}
                />
                {errors.email && (
                  <S.Error role="alert">E-post är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label>
                  Har sektionen sökt verksamhetsbidrag den senaste
                  verksamhetsperioden?
                </S.Label>
                <S.FlexWrapper>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("soktTidigarePeriod", { required: true })}
                      className={
                        errors.soktTidigarePeriod
                          ? "error custom-radio"
                          : "custom-radio"
                      }
                      onChange={() =>
                        updateFormProperty("soktTidigarePeriod", "Ja")
                      }
                    />
                    <S.SmallLabel htmlFor="andraBidrag">Ja</S.SmallLabel>
                  </div>
                  <div>
                    <S.RadioButton
                      type="radio"
                      {...register("soktTidigarePeriod", { required: true })}
                      className={
                        errors.soktTidigarePeriod
                          ? "error custom-radio"
                          : "custom-radio"
                      }
                      onChange={() =>
                        updateFormProperty("soktTidigarePeriod", "Nej")
                      }
                    />
                    <S.SmallLabel htmlFor="andraBidrag">Nej</S.SmallLabel>
                  </div>
                </S.FlexWrapper>
                {errors.soktTidigarePeriod && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="redovisaAktivitet" className="not-required">
                  Om ja, redovisa vilken aktivitet/aktiviteter ni har genomfört
                  som ni sökt medel för?
                </S.Label>
                <S.SmallLabel>
                  Beskriv även om aktivitet/aktiviteter inte genomförts.
                </S.SmallLabel>
                <S.TextArea
                  {...register("redovisaAktivitet", { required: false })}
                  placeholder="Redovisa aktivitet"
                  onChange={(e) =>
                    updateFormProperty("redovisaAktivitet", e.target.value)
                  }
                />
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="beviljadSumma" className="not-required">
                  Hur mycket verksamhetsbidrag blev ni beviljat den senaste
                  perioden?
                </S.Label>
                <S.Input
                  type="number"
                  {...register("beviljadSumma", { required: false })}
                  placeholder="Belopp"
                  onChange={(e) =>
                    updateFormProperty("beviljadSumma", e.target.value)
                  }
                />
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="anvandSumma" className="not-required">
                  Hur mycket av verksamhetsbidraget har ni använt den senaste
                  perioden?
                </S.Label>
                <S.Input
                  type="number"
                  {...register("anvandSumma", { required: false })}
                  placeholder="Belopp"
                  onChange={(e) =>
                    updateFormProperty("anvandSumma", e.target.value)
                  }
                />
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="aterbetalning" className="not-required">
                  Är återbetalning aktuellt?
                </S.Label>
                <S.SmallLabel>
                  Om ja, ange i siffror. Beskriv ifall ni önskar använda
                  eventuell överskott till kommande verksamhetsperiod.
                </S.SmallLabel>
                <S.Input
                  type="text"
                  {...register("aterbetalning", { required: false })}
                  placeholder="Beskrivning av återbetalning"
                  onChange={(e) =>
                    updateFormProperty("aterbetalning", e.target.value)
                  }
                />
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="verksamhet">
                  Beskriv verksamheten ni avser ansöka medel till
                </S.Label>
                <S.TextArea
                  {...register("verksamhet", { required: true })}
                  className={errors.verksamhet ? "error" : ""}
                  placeholder="Beskriv verksamhet"
                  onChange={(e) =>
                    updateFormProperty("verksamhet", e.target.value)
                  }
                />
                {errors.verksamhet && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="antalDeltagare">
                  Antal deltagare beräknas delta på dessa aktiviteter.
                </S.Label>
                <S.SmallLabel>
                  Skriv antal deltagare per aktivitet.
                </S.SmallLabel>
                <S.TextArea
                  {...register("antalDeltagare", { required: true })}
                  className={errors.antalDeltagare ? "error" : ""}
                  placeholder="Antal deltagare"
                  onChange={(e) =>
                    updateFormProperty("antalDeltagare", e.target.value)
                  }
                />
                {errors.antalDeltagare && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="kostnader">
                  Beskriv kostnaderna för aktiviteter ni ansöker medel till.
                </S.Label>
                <S.SmallLabel>Svara per aktivitet.</S.SmallLabel>
                <S.TextArea
                  {...register("kostnader", { required: true })}
                  className={errors.kostnader ? "error" : ""}
                  placeholder="Beskriv kostnader"
                  onChange={(e) =>
                    updateFormProperty("kostnader", e.target.value)
                  }
                />
                {errors.kostnader && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.Label htmlFor="totalKostnad">
                  Den totala summan ni ansöker till er verksamhet för denna
                  verksamhetsperiod.
                </S.Label>
                <S.Input
                  type="number"
                  {...register("totalKostnad", { required: true })}
                  className={errors.totalKostnad ? "error" : ""}
                  placeholder="Beskriv kostnader"
                  onChange={(e) =>
                    updateFormProperty("totalKostnad", e.target.value)
                  }
                />
                {errors.totalKostnad && (
                  <S.Error role="alert">Detta fält är obligatoriskt</S.Error>
                )}
              </S.Wrapper>
              <S.Wrapper>
                <S.CheckboxContainer>
                  <S.CheckboxWrapper>
                    <S.Checkbox
                      type="checkbox"
                      {...register("accept", { required: true })}
                      onChange={(e) =>
                        updateFormProperty("accept", e.target.checked)
                      }
                      className={errors.accept ? "error" : ""}
                    />
                    <svg
                      width="15"
                      height="13"
                      viewBox="0 0 15 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.1667 2.52002L5.37504 10.3117L1.83337 6.77002"
                        stroke="#281E78"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </S.CheckboxWrapper>
                  <S.SmallLabel htmlFor="accept">
                    Jag godkänner Stockholm Dövas Förenings{" "}
                    <a href="/">integritetspolicy.</a>
                  </S.SmallLabel>
                </S.CheckboxContainer>
              </S.Wrapper>
              <S.Wrapper>
                <S.SubmitButton type="submit">Skicka</S.SubmitButton>
                {formState.error && (
                  <S.FormError>
                    Något gick fel, vänligen försök igen.
                  </S.FormError>
                )}
              </S.Wrapper>
            </S.Form>
          </S.FormWrapper>
        )}
      </S.Inner>
    </MainSection>
  );
};

export default FormContributionSection;
